import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';
import {
  serializeManagementTimelineQuery,
  isSessionObservabilityProjection,
  type ActivityCockpitSnapshot,
  type CockpitPublicError,
  type CockpitUsageObservationState,
  type ControlPlaneManagementResponse,
  type ControlPlaneOperationalProjection,
  type SessionCapabilityEvidence,
  type ManagementTimelinePage,
  type SessionObservabilityProjection,
  type TraceArtifactContentPage,
} from '@internal/spassu-code-contract';
import type {
  CockpitArtifactContentQuery,
  CockpitDataSource,
  CockpitSnapshotQuery,
  CockpitTimelineQuery,
} from '@internal/spassu-code-cockpit-ui';

export interface ControlPlaneCockpitApi extends CockpitDataSource {}

export const controlPlaneCockpitApiRef = createApiRef<ControlPlaneCockpitApi>({
  id: 'plugin.spassu-code-control-plane.cockpit',
});

export class ControlPlaneCockpitApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly action: string | null,
  ) {
    super(message);
    this.name = 'ControlPlaneCockpitApiError';
  }
}

export class ControlPlaneCockpitClient implements ControlPlaneCockpitApi {
  private readonly sessionObservabilityByActivity = new Map<
    string,
    SessionObservabilityProjection
  >();

  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async getSnapshot({ activityId, revision, signal }: CockpitSnapshotQuery) {
    const snapshot = await this.get<ActivityCockpitSnapshot>(
      `${basePath(activityId)}${query({ revision })}`,
      signal,
    );
    try {
      const management = await this.get<ControlPlaneManagementResponse>(
        `/control-plane/activities/${encodeURIComponent(
          activityId,
        )}/management`,
        signal,
      );
      return this.withManagementObservation(snapshot, management, false);
    } catch {
      return this.withManagementObservation(snapshot, null, true);
    }
  }

  private withManagementObservation(
    snapshot: ActivityCockpitSnapshot,
    management: ControlPlaneManagementResponse | null,
    managementReadFailed: boolean,
  ): ActivityCockpitSnapshot {
    const current = management?.projection?.sessionObservability;
    const confirmed = isSessionObservabilityProjection(current)
      ? current
      : null;
    if (confirmed) {
      this.sessionObservabilityByActivity.set(snapshot.activityId, confirmed);
    }
    const lastValid =
      confirmed ?? this.sessionObservabilityByActivity.get(snapshot.activityId);
    return withManagementObservation(
      snapshot,
      management,
      managementReadFailed,
      lastValid,
      confirmed === null,
    );
  }

  getTimeline({ activityId, signal, ...queryInput }: CockpitTimelineQuery) {
    return this.get<ManagementTimelinePage>(
      `${basePath(activityId)}/timeline${query(
        serializeManagementTimelineQuery(queryInput),
      )}`,
      signal,
    );
  }

  getArtifactContent({
    activityId,
    artifactId,
    offset,
    limit,
    signal,
  }: CockpitArtifactContentQuery) {
    return this.get<TraceArtifactContentPage>(
      `${basePath(activityId)}/artifacts/${encodeURIComponent(
        artifactId,
      )}/content${query({ offset, limit })}`,
      signal,
    );
  }

  private async get<T>(path: string, signal?: AbortSignal): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('spassu-code');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, { signal });
    if (response.ok) return (await response.json()) as T;
    let error: Partial<CockpitPublicError> = {};
    try {
      error = (await response.json())?.error ?? {};
    } catch {
      // Public fallback below intentionally omits response payload details.
    }
    throw new ControlPlaneCockpitApiError(
      response.status,
      error.code ?? 'COCKPIT_REQUEST_FAILED',
      error.message ?? `Cockpit request failed (HTTP ${response.status}).`,
      error.action ?? null,
    );
  }
}

function withManagementObservation(
  snapshot: ActivityCockpitSnapshot,
  management: ControlPlaneManagementResponse | null,
  managementReadFailed: boolean,
  sessionObservability: SessionObservabilityProjection | undefined,
  sessionObservabilityUnconfirmed: boolean,
): ActivityCockpitSnapshot {
  const usage = snapshot.sections.usage.value;
  return {
    ...snapshot,
    ...(sessionObservability
      ? {
          sessionObservability: {
            projection: sessionObservability,
            stale: managementReadFailed || sessionObservabilityUnconfirmed,
          },
        }
      : {}),
    sections: {
      ...snapshot.sections,
      ...(usage?.diagnostics
        ? {
            usage: {
              ...snapshot.sections.usage,
              value: {
                ...usage,
                diagnostics: {
                  ...usage.diagnostics,
                  observationState: observationState(
                    snapshot,
                    management,
                    managementReadFailed,
                  ),
                },
              },
            },
          }
        : {
            usage: snapshot.sections.usage,
          }),
    },
  };
}

function observationState(
  snapshot: ActivityCockpitSnapshot,
  management: ControlPlaneManagementResponse | null,
  managementReadFailed: boolean,
): CockpitUsageObservationState {
  if (managementReadFailed) return 'stale';
  if (snapshot.revisionTrigger === 'manual-reconciliation') {
    return 'manual-reconciliation';
  }
  if (snapshot.revisionTrigger === 'late-evidence') return 'late-evidence';
  const projection = management?.projection;
  const session = projection?.sessionObservability;
  const sessionUsage = isSessionObservabilityProjection(session)
    ? session.capabilities.find(capability => capability.capability === 'usage')
        ?.state
    : undefined;
  return (
    observationFailure(projection, sessionUsage) ??
    telemetryObservation(projection) ??
    pendingObservation(projection, sessionUsage) ??
    snapshotObservation(snapshot)
  );
}

function observationFailure(
  projection: ControlPlaneOperationalProjection | undefined,
  sessionUsage: SessionCapabilityEvidence['state'] | undefined,
): CockpitUsageObservationState | undefined {
  const signalHealth = projection?.signalHealth?.condition;
  if (signalHealth === 'rejected' || sessionUsage === 'rejected')
    return 'rejected';
  if (signalHealth === 'stale') return 'stale';
  const collector = projection?.usageCollector?.state;
  if (collector === 'outdated' || sessionUsage === 'unsupported')
    return 'incompatible';
  if (collector === 'failed') return 'failed';
  return undefined;
}

function telemetryObservation(
  projection: ControlPlaneOperationalProjection | undefined,
): CockpitUsageObservationState | undefined {
  const telemetry = projection?.finalization?.telemetryState;
  if (
    telemetry === 'awaiting-stop' ||
    telemetry === 'flushing' ||
    telemetry === 'timed-out' ||
    telemetry === 'confirmed'
  )
    return telemetry;
  return undefined;
}

function pendingObservation(
  projection: ControlPlaneOperationalProjection | undefined,
  sessionUsage: SessionCapabilityEvidence['state'] | undefined,
): CockpitUsageObservationState | undefined {
  if (
    projection?.usageCollector?.state === 'pending' ||
    sessionUsage === 'pending'
  )
    return 'flushing';
  if (sessionUsage === 'confirmed') return 'confirmed';
  return undefined;
}

function snapshotObservation(
  snapshot: ActivityCockpitSnapshot,
): CockpitUsageObservationState {
  const hasUsage =
    snapshot.sections.usage.value?.inputTokens.value !== null ||
    snapshot.sections.usage.value?.outputTokens.value !== null;
  return hasUsage ? 'confirmed' : 'not-observed';
}

function basePath(activityId: string): string {
  return `/control-plane/activities/${encodeURIComponent(activityId)}/cockpit`;
}

function query(values: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined) params.set(key, String(value));
  });
  return params.size ? `?${params}` : '';
}
